import React, { useEffect, useState, useCallback } from "react";
import axios from "../../axiosInstace";
import "./JobApply.scss";

// ─── Applications page — HR view ──────────────────────────────────────────────
// Shows all job posts, each expandable to list applied candidates.
// Assumes endpoint: GET /api/hr/recruitment/apply/{jobPostId}
// Adjust APPLY_API path if your endpoint differs.

const POSTS_API = `${process.env.REACT_APP_API_HR_URL}/recruiter/recruitment/jobposts/all`;
const APPLY_API = `${process.env.REACT_APP_API_HR_URL}/recruitment/apply`;

const getInitials = (name = "") =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase() || "?";

const avatarColor = (name = "") => {
  const colors = [
    "#6366f1",
    "#0ea5e9",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#ec4899",
  ];
  return colors[(name.charCodeAt(0) || 0) % colors.length];
};

// ─────────────────────────────────────────────────────────────────────────────
//  Applications
// ─────────────────────────────────────────────────────────────────────────────
const Applications = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const fetchPosts = useCallback(() => {
    setLoading(true);
    axios
      .get(POSTS_API)
      .then((res) => setPosts(res.data.data || []))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const toggleExpand = (id) =>
    setExpandedId((prev) => (prev === id ? null : id));

  const filtered = posts.filter((p) => {
    const q = search.toLowerCase();
    return (
      p.jobTitle?.toLowerCase().includes(q) ||
      p.department?.toLowerCase().includes(q) ||
      p.roleName?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="ap-page">
      <div className="ap-page__header">
        <div>
          <h2 className="ap-page__title">Applications</h2>
          <p className="ap-page__sub">Candidates applied to your job posts</p>
        </div>
      </div>

      {/* Search */}
      <div className="ap-search-wrap">
        <svg
          className="ap-search-icon"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <circle
            cx="6.5"
            cy="6.5"
            r="4.5"
            stroke="#9ca3af"
            strokeWidth="1.5"
          />
          <path
            d="M10.5 10.5l3 3"
            stroke="#9ca3af"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
        <input
          className="ap-search"
          placeholder="Search job posts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button className="ap-search-clear" onClick={() => setSearch("")}>
            ×
          </button>
        )}
      </div>

      {loading ? (
        <div className="ap-loader">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="ap-empty">
          <span>📭</span>
          <p>
            {search ? "No job posts match your search." : "No job posts found."}
          </p>
        </div>
      ) : (
        <div className="ap-list">
          {filtered.map((p) => (
            <JobPostApplications
              key={p.jobPostId}
              post={p}
              isExpanded={expandedId === p.jobPostId}
              onToggle={() => toggleExpand(p.jobPostId)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  JobPostApplications — single job post row + expandable candidate list
// ─────────────────────────────────────────────────────────────────────────────
const JobPostApplications = ({ post, isExpanded, onToggle }) => {
  const [applicants, setApplicants] = useState([]);
  const [apLoading, setApLoading] = useState(false);
  const [apError, setApError] = useState(false);
  const [fetched, setFetched] = useState(false);
  // const [selectedApplicant, setSelectedApplicant] = useState(null);

  useEffect(() => {
    if (isExpanded && !fetched) {
      setApLoading(true);
      setApError(false);
      axios
        .get(`${APPLY_API}/${post.jobPostId}`)
        .then((res) => {
          setApplicants(res.data.data || []);
          setFetched(true);
        })
        .catch(() => setApError(true))
        .finally(() => setApLoading(false));
    }
  }, [isExpanded, fetched, post.jobPostId]);

  const statusColor = (s) =>
    ({
      OPEN: "ap-status-badge ap-status-badge--open",
      CLOSED: "ap-status-badge ap-status-badge--closed",
      DRAFT: "ap-status-badge ap-status-badge--draft",
    })[s] || "ap-status-badge";

  const appliedViaColor = (via) =>
    ({
      LINKEDLIN: "ap-via-tag ap-via-tag--linkedin",
      NAKURI: "ap-via-tag ap-via-tag--naukri",
      JOBDRIVE: "ap-via-tag ap-via-tag--drive",
      EXTERNAL: "ap-via-tag ap-via-tag--external",
      OTHER: "ap-via-tag ap-via-tag--other",
    })[via] || "ap-via-tag";

  return (
    <div className={`ap-card${isExpanded ? " ap-card--open" : ""}`}>
      {/* ── Row header ── */}
      <div className="ap-card__row" onClick={onToggle}>
        <div className="ap-card__left">
          <div className="ap-card__icon">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <rect
                x="2"
                y="2"
                width="14"
                height="14"
                rx="3"
                stroke="#2563eb"
                strokeWidth="1.5"
              />
              <path
                d="M5 7h8M5 11h5"
                stroke="#2563eb"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div>
            <div className="ap-card__title">{post.jobTitle}</div>
            <div className="ap-card__meta">
              {post.department && <span>{post.department}</span>}
              {post.roleName && <span>{post.roleName}</span>}
              {post.noOfVacancy && (
                <span>
                  {post.noOfVacancy} opening{post.noOfVacancy !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="ap-card__right">
          <span className={statusColor(post.status)}>{post.status}</span>
          {isExpanded && !apLoading && (
            <span className="ap-count-pill">
              {applicants.length} applicant{applicants.length !== 1 ? "s" : ""}
            </span>
          )}
          {/* Share link button */}
          {post.jobPostLink && (
            <button
              className="ap-copy-btn"
              title="Copy public link"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(post.jobPostLink).then(() => {
                  const btn = e.currentTarget;
                  btn.textContent = "Copied!";
                  setTimeout(() => {
                    btn.innerHTML = `<svg width="13" height="13" viewBox="0 0 13 13" fill="none"><rect x="1" y="3" width="8" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.2"/><path d="M4 3V2a1 1 0 011-1h5a1 1 0 011 1v7a1 1 0 01-1 1h-1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg> Copy Link`;
                  }, 2000);
                });
              }}
              dangerouslySetInnerHTML={{
                __html: `<svg width="13" height="13" viewBox="0 0 13 13" fill="none"><rect x="1" y="3" width="8" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.2"/><path d="M4 3V2a1 1 0 011-1h5a1 1 0 011 1v7a1 1 0 01-1 1h-1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg> Copy Link`,
              }}
            />
          )}
          <svg
            className={`ap-card__chevron${isExpanded ? " ap-card__chevron--open" : ""}`}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M4 6l4 4 4-4"
              stroke="#9ca3af"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/* ── Applicant list ── */}
      {isExpanded && (
        <div className="ap-applicants">
          {apLoading ? (
            <div className="ap-applicants__loading">
              <div className="ap-mini-spinner" />
              Loading applicants...
            </div>
          ) : apError ? (
            <div className="ap-applicants__error">
              Failed to load applicants.
              <button
                onClick={() => {
                  setFetched(false);
                }}
                className="ap-retry-btn"
              >
                Retry
              </button>
            </div>
          ) : applicants.length === 0 ? (
            <div className="ap-applicants__empty">
              <span>📭</span>
              <span>No applications yet for this job post.</span>
              {post.jobPostLink && (
                <a
                  href={post.jobPostLink}
                  target="_blank"
                  rel="noreferrer"
                  className="ap-share-link"
                >
                  Share job link ↗
                </a>
              )}
            </div>
          ) : (
            <>
              <div className="ap-applicants__header">
                <span className="ap-applicants__count">
                  {applicants.length} application
                  {applicants.length !== 1 ? "s" : ""}
                </span>
                {post.jobPostLink && (
                  <a
                    href={post.jobPostLink}
                    target="_blank"
                    rel="noreferrer"
                    className="ap-share-link"
                  >
                    View public form ↗
                  </a>
                )}
              </div>
              <div className="ap-table-wrap">
                <table className="ap-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Candidate</th>
                      <th>Contact</th>
                      <th>Qualification</th>
                      <th>Skills</th>
                      <th>Salary</th>
                      <th>Applied Via</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {applicants.map((a, i) => (
                      <tr
                        key={a.recuirtmentCandidateId || a.applicationId || i}
                      >
                        <td style={{ color: "#9ca3af", fontSize: 12 }}>
                          {i + 1}
                        </td>
                        <td>
                          <div className="ap-identity">
                            <div
                              className="ap-avatar"
                              style={{
                                background: avatarColor(a.candidateName),
                              }}
                            >
                              {getInitials(a.candidateName)}
                            </div>
                            <div>
                              <div className="ap-name">{a.candidateName}</div>
                              <div className="ap-email">{a.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="ap-contact">{a.contactNumber || "—"}</td>
                        <td>{a.qualification || "—"}</td>
                        <td>
                          <div className="ap-skills">
                            {a.skills ? (
                              a.skills
                                .split(",")
                                .map((s) => s.trim())
                                .filter(Boolean)
                                .slice(0, 2)
                                .map((s) => (
                                  <span key={s} className="ap-skill-tag">
                                    {s}
                                  </span>
                                ))
                            ) : (
                              <span style={{ color: "#d1d5db" }}>—</span>
                            )}
                            {a.skills?.split(",").length > 2 && (
                              <span className="ap-skill-tag ap-skill-tag--more">
                                +{a.skills.split(",").length - 2}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="ap-salary">
                          {a.expectedSalary
                            ? `₹${Number(a.expectedSalary).toLocaleString()}`
                            : "—"}
                        </td>
                        <td>
                          <span className={appliedViaColor(a.appliedVia)}>
                            {a.appliedVia || "—"}
                          </span>
                        </td>
                        <td>
                          {a.resumeUrl && (
                            <a
                              href={a.resumeUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="ap-resume-btn"
                            >
                              Resume ↗
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Applications;
